// ─── GrindLog — Supabase DB operations ───────────────────────────────────────
// This layer sits between Redux and Supabase.
// Redux is still the source of truth for UI state.
// Every mutation also calls these functions to persist remotely.

import { supabase } from './supabase';
import type { Session, Goal, AppSettings } from '../store';

// ─── Type helpers ─────────────────────────────────────────────────────────────
// DB uses snake_case, Redux uses camelCase — these mappers convert between them

function sessionToDb(s: Session, userId: string) {
  return {
    id:             s.id,
    user_id:        userId,
    date:           s.date,
    type:           s.type,
    platform:       s.platform,
    stakes:         s.stakes,
    buy_in:         s.buyIn,
    cash_out:       s.cashOut,
    tournaments:    s.tournaments,
    staking:        s.staking,
    total_invested: s.totalInvested,
    total_returned: s.totalReturned,
    gross_profit:   s.grossProfit,
    net_profit:     s.netProfit,
    duration:       s.duration,
    notes:          s.notes,
    tags:           s.tags,
  };
}

function sessionFromDb(row: any): Session {
  return {
    id:            row.id,
    date:          row.date,
    type:          row.type,
    platform:      row.platform,
    stakes:        row.stakes ?? '',
    buyIn:         Number(row.buy_in),
    cashOut:       Number(row.cash_out),
    tournaments:   row.tournaments ?? [],
    staking:       row.staking ?? { isStaked: false, stakerName: '', myPercent: 100 },
    totalInvested: Number(row.total_invested),
    totalReturned: Number(row.total_returned),
    grossProfit:   Number(row.gross_profit),
    netProfit:     Number(row.net_profit),
    duration:      row.duration,
    notes:         row.notes ?? '',
    tags:          row.tags ?? [],
  };
}

function goalToDb(g: Goal, userId: string) {
  return {
    id:           g.id,
    user_id:      userId,
    type:         g.type,
    description:  g.description,
    target_value: g.targetValue,
    deadline:     g.deadline ?? null,
    completed:    g.completed,
  };
}

function goalFromDb(row: any): Goal {
  return {
    id:          row.id,
    type:        row.type,
    description: row.description,
    targetValue: Number(row.target_value),
    deadline:    row.deadline ?? null,
    createdAt:   row.created_at,
    completed:   row.completed,
  };
}

// ─── Sessions ─────────────────────────────────────────────────────────────────

export async function fetchSessions(userId: string): Promise<Session[]> {
  const { data, error } = await supabase
    .from('sessions')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false });

  if (error) throw error;
  return (data ?? []).map(sessionFromDb);
}

export async function upsertSession(session: Session, userId: string): Promise<void> {
  const { error } = await supabase
    .from('sessions')
    .upsert(sessionToDb(session, userId), { onConflict: 'id' });

  if (error) throw error;
}

export async function upsertSessions(sessions: Session[], userId: string): Promise<void> {
  if (sessions.length === 0) return;
  const { error } = await supabase
    .from('sessions')
    .upsert(sessions.map(s => sessionToDb(s, userId)), { onConflict: 'id' });

  if (error) throw error;
}

export async function deleteSession(sessionId: string): Promise<void> {
  const { error } = await supabase
    .from('sessions')
    .delete()
    .eq('id', sessionId);

  if (error) throw error;
}

export async function deleteAllSessions(userId: string): Promise<void> {
  const { error } = await supabase
    .from('sessions')
    .delete()
    .eq('user_id', userId);

  if (error) throw error;
}

// ─── Goals ────────────────────────────────────────────────────────────────────

export async function fetchGoals(userId: string): Promise<Goal[]> {
  const { data, error } = await supabase
    .from('goals')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(goalFromDb);
}

export async function upsertGoal(goal: Goal, userId: string): Promise<void> {
  const { error } = await supabase
    .from('goals')
    .upsert(goalToDb(goal, userId), { onConflict: 'id' });

  if (error) throw error;
}

export async function deleteGoal(goalId: string): Promise<void> {
  const { error } = await supabase
    .from('goals')
    .delete()
    .eq('id', goalId);

  if (error) throw error;
}

// ─── Profile / Settings ───────────────────────────────────────────────────────

export async function fetchProfile(userId: string): Promise<AppSettings & { plan: 'free' | 'pro' }> {
  const { data, error } = await supabase
    .from('profiles')
    .select('initial_bankroll, currency, plan')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return {
    initialBankroll: Number(data.initial_bankroll),
    currency: data.currency,
    plan: data.plan as 'free' | 'pro',
  };
}

export async function updateProfile(
  userId: string,
  settings: Partial<AppSettings>
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({
      initial_bankroll: settings.initialBankroll,
      currency: settings.currency,
    })
    .eq('id', userId);

  if (error) throw error;
}
